/**
 * 从新的 VideoModelPublicInfo.modes 结构中提取当前模式下的能力
 *
 * 这个 hook 把新数据结构适配为组件消费的扁平化格式，
 * 减少 VideoPage 等消费端的改动量。
 */

import { useMemo } from 'react';
import type {
  VideoMode,
  VideoModelPublicInfo,
  DurationSpec,
} from '../services/videoApi';

export interface ModelCapabilitiesForMode {
  /** 该模式是否可用 */
  available: boolean;
  /** 音频生成 */
  enableAudio: boolean;
  /** 参考音频 */
  enableAudioReference: boolean;
  /** 镜头运动 */
  enableCameraMovement: boolean;
  /** 运动幅度 */
  enableMotionIntensity: boolean;
  /** 参考视频 */
  enableVideoReference: boolean;
  /** 时长声明 */
  supportedDurations: DurationSpec;
  /** 分辨率列表 */
  supportedResolutions: string[];
  /** 比例列表 */
  supportedAspectRatios: string[];
  /** 参考图上限 */
  maxReferenceImageCount: number;
  /** 若存在，则参考图数量必须命中该离散集合（例如 [1, 3]） */
  allowedReferenceImageCounts?: number[];
  /** 参考音频上限 */
  maxAudioReferenceCount: number;
  /** 参考音频最小时长（秒），未设置则不限制 */
  audioReferenceMinDurationSec?: number;
  /** 参考音频最大时长（秒），未设置则不限制 */
  audioReferenceMaxDurationSec?: number;
  /** 参考视频上限 */
  maxVideoReferenceCount: number;
  /** 参考视频单条最小时长（秒），未设置则不限制 */
  videoReferenceMinDurationSec?: number;
  /** 参考视频单条最大时长（秒），未设置则不限制 */
  videoReferenceMaxDurationSec?: number;
  /** 多条参考视频累计总时长上限（秒），未设置则不限制 */
  videoReferenceTotalMaxDurationSec?: number;
}

const EMPTY_CAPABILITIES: ModelCapabilitiesForMode = {
  available: false,
  enableAudio: false,
  enableAudioReference: false,
  enableCameraMovement: false,
  enableMotionIntensity: false,
  enableVideoReference: false,
  supportedDurations: { type: 'enum', values: [] },
  supportedResolutions: [],
  supportedAspectRatios: [],
  maxReferenceImageCount: 0,
  allowedReferenceImageCounts: undefined,
  maxAudioReferenceCount: 0,
  audioReferenceMinDurationSec: undefined,
  audioReferenceMaxDurationSec: undefined,
  maxVideoReferenceCount: 0,
  videoReferenceMinDurationSec: undefined,
  videoReferenceMaxDurationSec: undefined,
  videoReferenceTotalMaxDurationSec: undefined,
};

/**
 * 获取模型在指定模式下的能力
 */
export function getModelCapabilitiesForMode(
  model: VideoModelPublicInfo | undefined,
  mode: VideoMode
): ModelCapabilitiesForMode {
  if (!model) return EMPTY_CAPABILITIES;

  const modeInfo = model.modes.find((m) => m.mode === mode);
  if (!modeInfo) return EMPTY_CAPABILITIES;

  return {
    available: true,
    enableAudio: modeInfo.capabilities.audio ?? false,
    enableAudioReference: modeInfo.capabilities.audioReference ?? false,
    enableCameraMovement: modeInfo.capabilities.cameraMovement ?? false,
    enableMotionIntensity: modeInfo.capabilities.motionIntensity ?? false,
    enableVideoReference: modeInfo.capabilities.videoReference ?? false,
    supportedDurations: modeInfo.supportedDurations,
    supportedResolutions: modeInfo.supportedResolutions,
    supportedAspectRatios: modeInfo.supportedAspectRatios,
    maxReferenceImageCount: modeInfo.maxReferenceImageCount,
    allowedReferenceImageCounts: modeInfo.allowedReferenceImageCounts,
    maxAudioReferenceCount: modeInfo.maxAudioReferenceCount,
    audioReferenceMinDurationSec: modeInfo.audioReferenceMinDurationSec,
    audioReferenceMaxDurationSec: modeInfo.audioReferenceMaxDurationSec,
    maxVideoReferenceCount: modeInfo.maxVideoReferenceCount,
    videoReferenceMinDurationSec: modeInfo.videoReferenceMinDurationSec,
    videoReferenceMaxDurationSec: modeInfo.videoReferenceMaxDurationSec,
    videoReferenceTotalMaxDurationSec: modeInfo.videoReferenceTotalMaxDurationSec,
  };
}

/**
 * 获取模型所有可用模式列表
 */
export function getAvailableModes(model: VideoModelPublicInfo | undefined): VideoMode[] {
  if (!model) return [];
  return model.modes.map((m) => m.mode);
}

/**
 * 获取 DurationSpec 中的所有值列表（用于 UI 渲染按钮组或 slider 的 min/max）
 */
export function getDurationValuesFromSpec(spec: DurationSpec): number[] {
  if (spec.type === 'enum') return spec.values;
  const values: number[] = [];
  for (let v = spec.min; v <= spec.max; v += spec.step) {
    values.push(v);
  }
  return values;
}

/**
 * 检查某个时长值是否在 DurationSpec 范围内
 */
export function isDurationInSpec(spec: DurationSpec, value: number): boolean {
  if (spec.type === 'enum') return spec.values.includes(value);
  return value >= spec.min && value <= spec.max && (value - spec.min) % spec.step === 0;
}

/**
 * React Hook：监听模型和模式变化，返回当前能力
 */
export function useVideoModelCapabilities(
  model: VideoModelPublicInfo | undefined,
  mode: VideoMode
): ModelCapabilitiesForMode {
  return useMemo(() => getModelCapabilitiesForMode(model, mode), [model, mode]);
}
